"use client";
import SectionWrapper, { SectionWrapperProps } from "@cc/section-wrapper";
import { StaticImageData } from "next/image";
import { MaterialImage, MaterialImageProps } from "@cc/material-components";
import {
  List,
  ListItem,
  Stack,
  Typography,
  TypographyProps,
} from "@mui/material";
import { PlayArrowRounded } from "@mui/icons-material";
import { deepmerge } from "@mui/utils";
import { SvgIconProps } from "@mui/material/SvgIcon";
import { sxArrayUtil } from "@util/sx-helpers";

export interface InitiativePointsProps
  extends Omit<SectionWrapperProps, "children"> {
  points: string[];
  PointsIconProps?: Omit<SvgIconProps, "children">;
  PointsTypographyProps?: Omit<TypographyProps, "children">;
  title: string;
  TitleTypographyProps?: Omit<TypographyProps, "children">;
  image: string | StaticImageData;
  ImageProps?: Omit<MaterialImageProps, "src">;
}

const InitiativePoints = (props: InitiativePointsProps) => {
  const {
    points,
    title,
    image,
    ImageProps,
    TitleTypographyProps,
    PointsTypographyProps,
    PointsIconProps,
    ...restSectionWrapperProps
  } = props;
  return (
    <SectionWrapper
      {...deepmerge(defaultSectionWrapperProps, restSectionWrapperProps)}
    >
      <MaterialImage
        src={image}
        alt={title}
        {...ImageProps}
        sx={[
          {
            display: { xs: "none", xl: "block" },
            objectFit: "cover",
            maxWidth: 533,
            height: 422,
          },
          ...sxArrayUtil(ImageProps?.sx),
        ]}
      />
      <Stack rowGap={{ xs: 5, md: 3 }}>
        <Typography
          variant={"h3"}
          component={"h2"}
          textAlign={{ xs: "center", xl: "left" }}
          {...TitleTypographyProps}
        >
          {title}
        </Typography>
        <MaterialImage
          src={image}
          alt={title}
          {...ImageProps}
          sx={[
            {
              display: { xs: "block", xl: "none" },
              objectFit: "cover",
              height: 216,
            },
            ...sxArrayUtil(ImageProps?.sx),
          ]}
        />
        <List component={"ul"}>
          {points.map((point, idx) => (
            <ListItem
              key={idx}
              sx={{
                alignItems: "flex-start",
                columnGap: 1.5,
                px: 0,
              }}
            >
              <PlayArrowRounded
                fontSize={"small"}
                color={"primary"}
                {...PointsIconProps}
                sx={[
                  {
                    ml: -1,
                  },
                  ...sxArrayUtil(PointsIconProps?.sx),
                ]}
              />
              <Typography variant={"body1"} {...PointsTypographyProps}>
                {point}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Stack>
    </SectionWrapper>
  );
};

export default InitiativePoints;

const defaultSectionWrapperProps: Omit<SectionWrapperProps, "children"> = {
  direction: { xs: "column", xl: "row" },
  columnGap: 6,
};
